/**
 * The code. One field, built to be typed, pasted or filled by the phone (`one-time-code`), and submitted by itself
 * once all eight digits are there. Asking for another code waits a little (a fresh code invalidates nothing, but
 * every one spends the address's budget), and a passkey stays one tap away for someone who has one here after all.
 */
import { Icon, Spinner } from '@evt/flow-stream-ui'
import { type SubmitEvent, useEffect, useState } from 'react'
import { supportsPasskeys } from '../../auth/passkey'
import { CODE_LENGTH, normalizeCode, resendWait } from '../../auth/sign-in-flow'
import { useI18n } from '../../i18n'

export const RESEND_COOLDOWN_MS = 30_000

export function CodeStep({
  email,
  sentAt,
  busy,
  resent,
  onSubmit,
  onResend,
  onDifferentEmail,
  onPasskey
}: {
  email: string
  /** When the latest code was asked for (ms epoch). */
  sentAt: number
  busy: boolean
  /** A new code was just asked for (says so, once). */
  resent: boolean
  onSubmit: (code: string) => Promise<boolean>
  onResend: () => void
  onDifferentEmail: () => void
  onPasskey: () => void
}) {
  const { t } = useI18n()
  const [code, setCode] = useState('')
  const [now, setNow] = useState(() => Date.now())
  const wait = resendWait(sentAt, now, RESEND_COOLDOWN_MS)

  useEffect(() => {
    if (wait <= 0) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [wait])

  // A new code replaces the old one in the field.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on every new code, not on every render.
  useEffect(() => setCode(''), [sentAt])

  const submit = async (value: string) => {
    if (value.length !== CODE_LENGTH || busy) return
    const ok = await onSubmit(value)
    if (!ok) setCode('') // a wrong code is retyped, not edited
  }

  return (
    <div className="login-body">
      <p className="login-lead">
        {t.login.codeSentBefore} <strong>{email}</strong>
        {t.login.codeSentAfter}
      </p>
      <form
        className="login-form"
        onSubmit={(e: SubmitEvent) => {
          e.preventDefault()
          void submit(code)
        }}
      >
        <label className="field-label" htmlFor="signin-code">
          {t.login.verificationCode}
        </label>
        <input
          id="signin-code"
          name="code"
          className="login-input login-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          // biome-ignore lint/a11y/noAutofocus: the code is the only thing this step asks for.
          autoFocus
          required
          placeholder="••••••••"
          aria-describedby="signin-code-help"
          value={code}
          onChange={(e) => {
            const next = normalizeCode(e.target.value)
            setCode(next)
            if (next.length === CODE_LENGTH) void submit(next)
          }}
        />
        <button type="submit" className="btn btn-primary btn-block" disabled={busy || code.length !== CODE_LENGTH}>
          {busy ? <Spinner size={14} /> : <Icon name="check" size={14} />} {t.login.signIn}
        </button>
      </form>
      <p id="signin-code-help" className="login-hint" aria-live="polite">
        {resent ? t.login.codeResent : t.login.codeArrives}
      </p>
      <div className="login-row">
        <button type="button" className="login-link" disabled={busy || wait > 0} onClick={onResend}>
          {wait > 0 ? t.login.resendIn(wait) : t.login.resend}
        </button>
        <button type="button" className="login-link" disabled={busy} onClick={onDifferentEmail}>
          {t.login.useDifferentEmail}
        </button>
      </div>
      {supportsPasskeys() && (
        <button type="button" className="btn btn-ghost btn-block btn-sm" disabled={busy} onClick={onPasskey}>
          <Icon name="shield" size={13} /> {t.login.usePasskeyInstead}
        </button>
      )}
    </div>
  )
}
